/**
 * UsersTab — Tabelle aller Benutzer mit Rolle, Scope und Aktionen.
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Pencil, Trash2, UserPlus } from 'lucide-react';
import { formatLastFirst } from '../../lib/nameFormat';

const getScopeLabel = (perm) => {
  if (perm?.all_branches || perm?.all_locations) return 'Alle Bereiche';
  const branchCount = perm?.branch_ids?.length || 0;
  const locationCount = perm?.location_ids?.length || 0;
  if (branchCount > 0 && locationCount > 0) return `${branchCount} NL, ${locationCount} Std.`;
  if (branchCount > 0) return `${branchCount} Niederlassung(en)`;
  if (locationCount > 0) return `${locationCount} Standort(e)`;
  return 'Nicht definiert';
};

export const UsersTab = ({
  users, permissions, roles, currentUser,
  onEdit, onDelete, onCleanupOrphans, onNewUser,
}) => {
  const getPerm = (uid) => permissions.find((p) => p.user_id === uid);
  const getRole = (rid) => roles.find((r) => r.id === rid);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Benutzer & Berechtigungen</CardTitle>
          <CardDescription>Weisen Sie Benutzern Rollen und Zugriffsbereich zu</CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={onCleanupOrphans} data-testid="btn-cleanup-orphans">
            <Trash2 className="h-4 w-4 mr-2" /> Verwaiste bereinigen
          </Button>
          <Button onClick={onNewUser} data-testid="btn-new-user">
            <UserPlus className="h-4 w-4 mr-2" /> Neuer Benutzer
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Benutzer</TableHead>
              <TableHead>Rolle</TableHead>
              <TableHead>Zustaendigkeitsbereich</TableHead>
              <TableHead className="text-right">Aktionen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => {
              const perm = getPerm(u.id);
              const role = perm?.role_id ? getRole(perm.role_id) : null;
              return (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium"
                        style={{ backgroundColor: role?.color || '#6B7280' }}
                      >
                        {u.first_name?.[0]}{u.last_name?.[0]}
                      </div>
                      <div>
                        <p className="font-medium">{formatLastFirst(u)}</p>
                        <p className="text-sm text-muted-foreground">{u.email}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {role ? (
                      <Badge variant="secondary" style={{
                        backgroundColor: `${role.color}20`, color: role.color, borderColor: role.color,
                      }}>{role.name}</Badge>
                    ) : (
                      <span className="text-muted-foreground text-sm">Keine Rolle</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{getScopeLabel(perm)}</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button variant="outline" size="sm" onClick={() => onEdit(u)}
                        data-testid={`btn-edit-user-${u.id}`}>
                        <Pencil className="h-4 w-4 mr-2" /> Bearbeiten
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => onDelete(u)}
                        disabled={u.id === currentUser?.id}
                        title={u.id === currentUser?.id ? 'Sie können sich nicht selbst löschen' : 'Benutzer löschen'}
                        data-testid={`btn-delete-user-${u.id}`}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};
